<template>
  <header ref="topbar" class="incremental-topbar" :class="{ 'incremental-topbar--dark': modernContent && modernDark }" :data-experience="experience" @keydown.esc.stop.prevent="closeMenu(true)">
    <router-link class="incremental-topbar__brand" :to="{ name: navigation[0].item.routeName }" aria-label="Eurekers, ir al graficador" @click.native="closeMenu(false)">Eurekers<span aria-hidden="true">.</span></router-link>

    <button class="incremental-topbar__mobile-trigger" type="button" :aria-expanded="openPanel === 'navigation' ? 'true' : 'false'"
      aria-controls="incremental-mobile-navigation" aria-label="Abrir navegación" @click="togglePanel('navigation', $event)">
      <span class="material-icons-outlined" aria-hidden="true">{{ openPanel === 'navigation' ? 'close' : 'menu' }}</span><span>Menú</span>
    </button>

    <nav class="incremental-topbar__desktop" aria-label="Navegación principal">
      <div v-for="group in navigation" :key="group.id" class="incremental-nav-group">
        <router-link v-if="group.item" :to="{ name: group.item.routeName }" class="incremental-topbar__tab" :data-route="group.item.routeName"
          :class="{ 'is-active': isGroupActive(group) }" :aria-current="isGroupActive(group) ? 'page' : null" @click.native="closeMenu(false)">
          {{ group.label }}<span v-if="group.id === 'forum' && forumBadge" class="incremental-topbar__dot" :aria-label="forumBadge + ' avisos de foro pendientes'"></span>
        </router-link>
        <template v-else>
          <button type="button" class="incremental-topbar__tab" :data-nav-group="group.id" :class="{ 'is-active': isGroupActive(group), 'is-open': openPanel === group.id }"
            :aria-expanded="openPanel === group.id ? 'true' : 'false'" :aria-controls="'incremental-panel-' + group.id"
            @click="togglePanel(group.id, $event)" @keydown.down.prevent="openAndFocus(group.id, $event)">
            {{ group.label }}<span class="material-icons-outlined incremental-topbar__chevron" aria-hidden="true">expand_more</span>
          </button>
          <div v-if="openPanel === group.id" :id="'incremental-panel-' + group.id" class="incremental-dropdown incremental-dropdown--group"
            :class="{ 'incremental-dropdown--wide': group.sections.length > 1 }" @keydown="onPanelKeydown">
            <section v-for="section in group.sections" :key="section.label" class="incremental-dropdown__section">
              <h2>{{ section.label }}</h2>
              <IncrementalNavLink v-for="item in section.items" :key="item.id" :item="item" @navigate="closeMenu(false)" />
            </section>
          </div>
        </template>
      </div>
    </nav>

    <button ref="accountTrigger" type="button" class="incremental-account-trigger" :aria-expanded="openPanel === 'account' ? 'true' : 'false'"
      aria-controls="incremental-panel-account" :aria-label="accountLabel" @click="togglePanel('account', $event)" @keydown.down.prevent="openAndFocus('account', $event)">
      <span v-if="loggedIn" class="incremental-account-trigger__initials">{{ identity.initials }}</span>
      <span v-else class="material-icons-outlined" aria-hidden="true">person_outline</span>
      <span v-if="unreadCount" class="incremental-account-trigger__badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
    </button>

    <nav v-if="openPanel === 'navigation'" id="incremental-mobile-navigation" class="incremental-dropdown incremental-mobile-navigation" aria-label="Navegación principal móvil">
      <div v-for="group in navigation" :key="group.id" class="incremental-mobile-navigation__group">
        <IncrementalNavLink v-if="group.item" :item="group.item" :badge="group.id === 'forum' ? forumBadge : 0" @navigate="closeMenu(false)" />
        <template v-else>
          <button type="button" class="incremental-mobile-navigation__heading" :class="{ 'is-current': isGroupActive(group) }"
            :aria-expanded="mobileGroup === group.id ? 'true' : 'false'" :aria-controls="'incremental-mobile-' + group.id" @click="mobileGroup = mobileGroup === group.id ? '' : group.id">
            {{ group.label }}<span class="material-icons-outlined" aria-hidden="true">{{ mobileGroup === group.id ? 'expand_less' : 'expand_more' }}</span>
          </button>
          <div v-if="mobileGroup === group.id" :id="'incremental-mobile-' + group.id" class="incremental-mobile-navigation__items">
            <section v-for="section in group.sections" :key="section.label">
              <h2>{{ section.label }}</h2>
              <IncrementalNavLink v-for="item in section.items" :key="item.id" :item="item" @navigate="closeMenu(false)" />
            </section>
          </div>
        </template>
      </div>
    </nav>

    <section v-if="openPanel === 'account'" id="incremental-panel-account" class="incremental-dropdown incremental-account" aria-label="Mi cuenta y opciones" @keydown="onPanelKeydown">
      <div class="incremental-account__identity">
        <span class="incremental-account__avatar" aria-hidden="true">{{ loggedIn ? identity.initials : 'E' }}</span>
        <div><strong>{{ loggedIn ? identity.name : 'Bienvenido a Eurekers' }}</strong><span>{{ loggedIn ? identity.plan || 'Mi cuenta' : 'Accede a tu cuenta' }}</span></div>
      </div>
      <ModernNotificationsPreview v-if="modernContent && loggedIn" :state="notificationController.state" @mark-read="markAllNotificationsRead" />
      <div class="incremental-account__section">
        <template v-if="loggedIn">
          <IncrementalNavLink v-for="item in accountItems" :key="item.id" :item="item" @navigate="closeMenu(false)" />
          <IncrementalNavLink :item="{ routeName: 'Pricing', name: 'Suscripción y planes', icon: 'loyalty' }" @navigate="closeMenu(false)" />
        </template>
        <IncrementalNavLink v-else :item="{ routeName: 'Login', name: 'Iniciar sesión', icon: 'login' }" @navigate="closeMenu(false)" />
        <IncrementalNavLink v-for="item in accountExtras" :key="item.id" :item="item" @navigate="closeMenu(false)" />
      </div>
      <div class="incremental-account__section">
        <button v-if="loggedIn" type="button" class="incremental-account__action" @click="openNotifications">
          <span class="material-icons-outlined" aria-hidden="true">notifications_none</span><span>Notificaciones</span>
          <span v-if="unreadCount" class="incremental-nav-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
          <small v-else-if="notificationsLoading">Cargando…</small>
        </button>
        <button v-if="notificationError" type="button" class="incremental-account__retry" @click="refreshNotifications">No se pudieron cargar los avisos. Reintentar</button>
        <IncrementalNavLink :item="helpItem" @navigate="closeMenu(false)" />
        <button v-if="canChat" id="chat-selector" type="button" class="incremental-account__action" :disabled="chatOpening" :aria-busy="chatOpening ? 'true' : 'false'" @click="openChat">
          <span class="material-icons-outlined" aria-hidden="true">chat_bubble_outline</span><span>{{ chatOpening ? 'Abriendo chat…' : 'Chat de ayuda' }}</span>
        </button>
      </div>
      <div class="incremental-account__section">
        <div v-if="modernContent" class="incremental-account__themes" role="group" aria-label="Tema">
          <button v-for="theme in themes" :key="theme.value" type="button" :data-theme="theme.value" :aria-pressed="String(preferenceState.preferences.themeMode === theme.value)"
            :class="{ 'is-selected': preferenceState.preferences.themeMode === theme.value }" :disabled="themeChanging" @click="changeModernTheme(theme.value)">{{ theme.label }}</button>
        </div>
        <button v-else id="theme-selector" type="button" class="incremental-account__action" :disabled="themeChanging" :aria-busy="themeChanging ? 'true' : 'false'" @click="changeInterfacePlatinum">
          <span class="material-icons-outlined" aria-hidden="true">{{ isDark ? 'light_mode' : 'dark_mode' }}</span><span>{{ themeChanging ? 'Cambiando tema…' : isDark ? 'Usar tema claro' : 'Usar tema oscuro' }}</span>
        </button>
        <button type="button" class="incremental-account__action" :disabled="!fullscreenState.supported" @click="toggleFullscreen">
          <span class="material-icons-outlined" aria-hidden="true">{{ fullscreenState.active ? 'fullscreen_exit' : 'fullscreen' }}</span><span>{{ fullscreenState.active ? 'Salir de pantalla completa' : 'Pantalla completa' }}</span>
        </button>
        <button v-if="previewAllowed" type="button" class="incremental-account__action" data-action="switch-experience" :disabled="previewOpening" @click="switchExperience">
          <span class="material-icons-outlined" aria-hidden="true">{{ experience === 'modern' ? 'history' : 'science' }}</span><span>{{ previewOpening ? 'Cambiando…' : experience === 'modern' ? 'Cambiar a la versión clásica' : 'Probar nueva experiencia' }}<small>{{ experience === 'modern' ? 'Contenido nuevo activado' : 'Laboratorio · solo administración' }}</small></span>
        </button>
        <p v-if="preferenceError" class="incremental-account__error" role="status">{{ preferenceError }}</p>
      </div>
      <div v-if="loggedIn" class="incremental-account__section incremental-account__session">
        <button type="button" class="incremental-account__action" :disabled="loggingOut" @click="logout"><span class="material-icons-outlined" aria-hidden="true">logout</span><span>{{ loggingOut ? 'Cerrando sesión…' : 'Cerrar sesión' }}</span></button>
      </div>
    </section>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';
import IncrementalNavLink from './IncrementalNavLink.vue';
import { currentNavigation, currentAccountExtras, INCREMENTAL_ACCOUNT_ITEMS, HELP_ITEM, isNavigationItemActive } from './models/incrementalNavigation';
import repository from './api/incrementalNavigationRepository';
import LegacyThemeSwitcher from '@/shared/mixins/LegacyThemeSwitcher';
import { modernAccountIdentity } from '@/shells/models/modernAccount';
import { hasModernPreviewAccess } from '@/shells/models/modernPreviewAccess';
import { modernChatService, rolesAllowModernChat } from '@/shells/services/modernChatService';
import { FullscreenController } from '@/shells/services/fullscreenController';
import { interfacePreferencesController } from '@/shells/services/interfacePreferencesController';
import { ModernNotificationsController } from '@/shells/services/modernNotificationsController';
import ModernNotificationsPreview from '@/shells/modern/ModernNotificationsPreview.vue';
import { availableExperience, explicitContentExperience, experienceDestination } from './models/contentExperience';
import VueRouter from 'vue-router';

export default {
  name: 'IncrementalNavigation',
  components: { IncrementalNavLink, ModernNotificationsPreview },
  props: { modernContent: { type: Boolean, default: false }, modernDark: { type: Boolean, default: false } },
  mixins: [LegacyThemeSwitcher],
  data: () => ({
    openPanel: '', mobileGroup: '', username: '', accountItems: INCREMENTAL_ACCOUNT_ITEMS, helpItem: HELP_ITEM,
    settings: { referralsEnabled: false, newsflashPublicAccessEnabled: false },
    unreadCount: 0, notificationsLoading: false, notificationError: false, notifications_opened: false,
    forumUnread: 0, showForumNotifications: true, chatOpening: false, loggingOut: false, previewOpening: false,
    sessionExperience: null, switchingFrom: null, preferenceError: '',
    themes: [{ value: 'light', label: 'Claro' }, { value: 'dark', label: 'Oscuro' }, { value: 'system', label: 'Automático' }],
    notificationController: new ModernNotificationsController(),
    fullscreenController: new FullscreenController()
  }),
  computed: {
    ...mapGetters(['_g_User', '_g_UserId', '_g_IsUserLogged', '_g_UserFeatures', '_g_ThemeName']),
    user() { return this._g_User || {}; },
    uid() { return this._g_UserId || this.user.uid || 0; },
    // Off-plan accounts are still authenticated and must retain logout/account access.
    loggedIn() { return Number(this.uid) > 0; },
    identity() { return modernAccountIdentity({ ...this.user, name: this.username || this.user.name }); },
    preferenceState() { return interfacePreferencesController.stateFor(this.uid); },
    experience() { return availableExperience(this.switchingFrom || this.sessionExperience || this.preferenceState.preferences.shell, this.user); },
    navigation() { return currentNavigation(this.user, this._g_UserFeatures || {}, this.settings, this.experience); },
    accountExtras() { return currentAccountExtras(this.user, this._g_UserFeatures || {}, this.settings); },
    accountLabel() { return this.unreadCount ? `Mi cuenta, ${this.unreadCount} avisos pendientes` : 'Mi cuenta y opciones'; },
    forumBadge() { return this.showForumNotifications ? this.forumUnread : 0; },
    isDark() { return this.modernContent ? this.modernDark : this._g_ThemeName === 'dark-theme'; },
    previewAllowed() { return hasModernPreviewAccess(this.user); },
    canChat() { return rolesAllowModernChat(this.user.roles); },
    fullscreenState() { return this.fullscreenController.state; }
  },
  watch: {
    '$route.fullPath'() {
      this.closeMenu(false);
      this.sessionExperience = explicitContentExperience(this.$route) || this.sessionExperience;
    },
    uid() { this.loadUser(); },
    modernContent() {
      this.sessionRevision++;
      this.notificationsLoading = false; this.notificationError = false; this.unreadCount = 0;
      this.refreshNotifications();
    }
  },
  created() {
    this.sessionRevision = 0;
    this.forumCountRevision = 0;
    this.alive = true;
    this.lastTrigger = null;
  },
  mounted() {
    repository.getSettings().then(settings => { if (this.alive) this.settings = settings; });
    try { this.showForumNotifications = localStorage.getItem('show_forum_notifications_menu') !== 'false'; } catch (_) { /* Private browsing. */ }
    this.loadUser();
    this.fullscreenController.mount(document.documentElement);
    document.addEventListener('pointerdown', this.onOutsidePointer);
    document.addEventListener('focusin', this.onOutsideFocus);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('blur', this.onWindowBlur);
    this.$root.$on('forumNotificationSettingChanged', this.onForumNotificationSetting);
    this.$root.$on('userActivityUpdated', this.onForumActivityUpdated);
  },
  beforeDestroy() {
    this.alive = false;
    this.sessionRevision++;
    this.fullscreenController.unmount();
    document.removeEventListener('pointerdown', this.onOutsidePointer);
    document.removeEventListener('focusin', this.onOutsideFocus);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('blur', this.onWindowBlur);
    this.$root.$off('forumNotificationSettingChanged', this.onForumNotificationSetting);
    this.$root.$off('userActivityUpdated', this.onForumActivityUpdated);
  },
  methods: {
    isGroupActive(group) {
      return (group.item ? [group.item] : group.sections.reduce((all, part) => all.concat(part.items), [])).some(item => isNavigationItemActive(item, this.$route));
    },
    togglePanel(id, event) {
      const opening = this.openPanel !== id;
      this.lastTrigger = event && event.currentTarget;
      this.openPanel = opening ? id : '';
      if (opening && id === 'account') {
        this.refreshNotifications();
        this.$nextTick(() => {
          const first = document.querySelector('#incremental-panel-account a,#incremental-panel-account button:not([disabled])');
          if (first) first.focus();
        });
      }
      if (opening && id === 'navigation') {
        const active = this.navigation.find(group => group.sections && this.isGroupActive(group));
        this.mobileGroup = active ? active.id : '';
      }
    },
    openAndFocus(id, event) {
      this.lastTrigger = event.currentTarget;
      this.openPanel = id;
      if (id === 'account') this.refreshNotifications();
      this.$nextTick(() => {
        const panel = document.getElementById('incremental-panel-' + id);
        const first = panel && panel.querySelector('a,button:not([disabled])');
        if (first) first.focus();
      });
    },
    closeMenu(restoreFocus = false) {
      if (!this.openPanel) return;
      this.openPanel = '';
      if (restoreFocus && this.lastTrigger) this.$nextTick(() => this.lastTrigger && this.lastTrigger.isConnected && this.lastTrigger.focus());
    },
    onPanelKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || /input|textarea|select/i.test(event.target.tagName)) return;
      const controls = Array.from(event.currentTarget.querySelectorAll('a,button:not([disabled])'));
      const index = controls.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? controls.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + controls.length) % controls.length;
      if (controls[next]) { event.preventDefault(); controls[next].focus(); }
    },
    onOutsidePointer(event) { if (this.$refs.topbar && !this.$refs.topbar.contains(event.target)) this.closeMenu(false); },
    onOutsideFocus(event) { this.onOutsidePointer(event); },
    onResize() { this.closeMenu(false); },
    onWindowBlur() { this.closeMenu(false); },
    onForumNotificationSetting(enabled) {
      this.showForumNotifications = Boolean(enabled);
      try { localStorage.setItem('show_forum_notifications_menu', String(Boolean(enabled))); } catch (_) { /* Private browsing. */ }
    },
    onForumActivityUpdated(kind, result) {
      if (kind !== 'notification') return;
      if (result) {
        if (String(result.uid) !== String(this.uid) || !Number.isSafeInteger(result.unread) || result.unread < 0) return;
        this.forumCountRevision++; this.forumUnread = result.unread;
      } else this.refreshForumUnread();
    },
    refreshForumUnread() {
      const revision = ++this.forumCountRevision, session = this.sessionRevision;
      if (!this.loggedIn || !Number(this.uid)) return;
      repository.getForumUnreadCount().then(count => {
        if (this.alive && revision === this.forumCountRevision && session === this.sessionRevision) this.forumUnread = count;
      }).catch(() => {});
    },
    loadUser() {
      const revision = ++this.sessionRevision;
      this.unreadCount = 0; this.forumUnread = 0; this.username = '';
      this.notificationsLoading = false; this.notificationError = false;
      this.sessionExperience = explicitContentExperience(this.$route);
      this.preferenceError = '';
      this.notificationController = new ModernNotificationsController();
      if (!this.loggedIn || !Number(this.uid)) return;
      if (this.previewAllowed) interfacePreferencesController.load(this.uid).catch(() => {
        if (this.alive && revision === this.sessionRevision) this.preferenceError = 'No se pudo recuperar la preferencia de experiencia. Puedes reintentar el cambio.';
      });
      repository.getName().then(name => { if (this.alive && revision === this.sessionRevision) this.username = name; }).catch(() => {});
      this.refreshForumUnread();
      this.refreshNotifications();
      // Preserve the menu-only chat launcher, including widgets already loaded by Drupal.
      if (this.canChat) modernChatService.load().catch(() => {});
    },
    async refreshNotifications(force = false) {
      if (!this.loggedIn || !Number(this.uid) || this.notificationsLoading) return;
      const revision = this.sessionRevision;
      this.notificationsLoading = true;
      this.notificationError = false;
      try {
        const count = this.modernContent
          ? (await this.notificationController.load({ force: force === true })).unreadCount
          : await repository.getUnreadCount(this.uid);
        if (this.alive && revision === this.sessionRevision) this.unreadCount = count;
      } catch (_) { if (this.alive && revision === this.sessionRevision) this.notificationError = true; }
      finally { if (this.alive && revision === this.sessionRevision) this.notificationsLoading = false; }
    },
    async markAllNotificationsRead() {
      try { await this.notificationController.markAllRead(); this.unreadCount = this.notificationController.state.unreadCount; }
      catch (_) { this.feedback('No se pudieron marcar los avisos como leídos.'); }
    },
    openNotifications() {
      this.closeMenu(false);
      this.notifications_opened = true;
      this.$emit('open_notification_container');
    },
    async closeNotifications() {
      this.notifications_opened = false;
      this.$emit('close_notification_container');
      try { await repository.markNotificationsRead(this.uid); await this.refreshNotifications(true); }
      catch (_) { this.feedback('No se pudieron marcar los avisos como leídos.'); }
      this.$nextTick(() => this.$refs.accountTrigger && this.$refs.accountTrigger.focus());
    },
    async openChat() {
      if (!this.canChat || this.chatOpening) return;
      this.chatOpening = true;
      try { await modernChatService.open(); this.closeMenu(false); }
      catch (_) { this.feedback('No se pudo abrir el chat. Pulsa Chat para reintentarlo.'); }
      finally { this.chatOpening = false; }
    },
    async toggleFullscreen() {
      try { await this.fullscreenController.toggle(); this.closeMenu(false); }
      catch (_) { this.feedback('No se pudo cambiar el modo de pantalla.'); }
    },
    async changeModernTheme(themeMode) {
      if (this.themeChanging) return;
      this.themeChanging = true;
      try { await interfacePreferencesController.load(this.uid).catch(() => {}); await interfacePreferencesController.update(this.uid, { themeMode }); }
      catch (_) { this.feedback('No se pudo guardar el tema.'); }
      finally { this.themeChanging = false; }
    },
    async switchExperience() {
      if (!this.previewAllowed || this.previewOpening) return;
      const next = this.experience === 'modern' ? 'legacy' : 'modern';
      const route = this.$route;
      const uid = this.uid;
      const previousExperience = this.experience;
      let persisted = false;
      this.switchingFrom = this.experience;
      this.previewOpening = true;
      this.preferenceError = '';
      try {
        await interfacePreferencesController.load(uid).catch(() => {});
        if (!this.alive || this.uid !== uid || !this.previewAllowed) return;
        await interfacePreferencesController.update(uid, { shell: next });
        persisted = true;
        if (!this.alive || this.uid !== uid || !this.previewAllowed) return;
        this.sessionExperience = next;
        this.closeMenu(false);
        const destination = experienceDestination(route, next);
        if (destination && this.$route.fullPath === route.fullPath && destination.name !== route.name) await this.$router.push(destination);
        this.$emit('experience-change', next);
      } catch (error) {
        if (persisted && this.alive && this.uid === uid && this.$route.fullPath === route.fullPath) {
          this.sessionExperience = previousExperience;
          try { await interfacePreferencesController.update(uid, { shell: previousExperience }); }
          catch (_) {
            this.preferenceError = 'Sigues en esta versión, pero no se pudo restaurar la preferencia guardada. Vuelve a intentarlo.';
            this.feedback(this.preferenceError); return;
          }
          if (VueRouter.isNavigationFailure(error, VueRouter.NavigationFailureType.aborted)) return;
        }
        this.preferenceError = 'No se pudo cambiar de experiencia. Inténtalo de nuevo.';
        this.feedback(this.preferenceError);
      }
      finally { this.previewOpening = false; this.switchingFrom = null; }
    },
    async logout() {
      if (this.loggingOut) return;
      this.loggingOut = true;
      try { await repository.logout(); localStorage.removeItem('user_access_token'); window.location.reload(); }
      catch (_) { this.feedback('No se pudo cerrar la sesión. Inténtalo de nuevo.'); }
      finally { this.loggingOut = false; }
    },
    feedback(message) { if (this.$vueOnToast) this.$vueOnToast.pop('error', message); }
  }
};
</script>

<style lang="scss" src="./incrementalNavigation.scss"></style>
